import React, {FC, ReactNode, useState} from 'react';
import {animated, useTransition} from 'react-spring';
import styled from 'styled-components';

import {Renderer} from '../../../libs/shared/core/src/helpers/react/reactHelpers';
import {blendColors} from '../../../libs/shared/core/src/models/helpers/colorHelpers';
import {ChromelessButton} from '../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {Loader, LoaderVariantsEnum} from '../../../libs/shared/ui/src/components/loaders/loader';
import {TooltipCoordinator} from '../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {boxShadow} from '../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {UserTheme} from '../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';

/*
 * Props.
 */

export interface OnboardingGuideTaskProps {
  title: ReactNode;
  description: ReactNode;
  isComplete: boolean;
  onClick: () => Promise<void> | void;
  stepNum: number;
  isCompact: boolean;
  renderDetails?: Renderer;
}

/*
 * Style.
 */

interface PropsWithIsComplete {
  $isComplete: boolean;
  $isCompact: boolean;
}

const StyledChromelessButton = styled(ChromelessButton)<PropsWithIsComplete>`
  border-radius: 12px;
  background: ${(p) => p.theme.foreground};
  ${(p) => boxShadow(p.theme, `0 1px 9px ${p.theme.alphas.black20}`)};
  border: ${(p) => `1px solid ${p.theme.alphas.black20}`};
  padding: ${(p) => (p.$isCompact ? '11px' : '15px')} 15px;

  position: relative;
  display: grid;
  grid-template-areas: 'checkbox . content chevron';
  grid-template-columns: 16px 8px 1fr 30px;

  margin: 0;
  overflow: hidden;

  width: 100%;
  align-items: center;

  &:hover {
    background-color: ${(p) => blendColors(p.theme.foreground, p.theme.alphas.gray10)};
  }
`;

const StyledTaskTitle = styled.h3`
  margin: 0;
  font-size: ${fontSizes.medium};
  color: ${(p) => p.theme.greys.shade90};
  font-weight: ${fontWeights.semibold};
  line-height: 16px;
`;

const StyledTaskDescription = styled.p`
  font-size: ${fontSizes.verySmall};
  color: ${(p) => p.theme.greys.shade70};
  font-weight: ${fontWeights.normal};
  line-height: 16px;
  margin-top: 4px;
  margin-bottom: 0;
`;

const StyledContentWrapper = styled.div`
  grid-area: content;

  display: flex;
  flex-direction: column;
`;

const StyledTextWrapper = styled.div`
  flex-grow: 1;
`;

const StyledTaskCircleDiv = styled.div`
  border-radius: 50%;
  border: 1.5px solid ${(p) => p.theme.greys.shade70};
  color: ${(p) => p.theme.greys.shade70};

  width: 16px;
  height: 16px;
  font-size: 9px;
  font-weight: ${fontWeights.black};
  line-height: 1.3;

  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledTaskCircleContainerDiv = styled.div`
  grid-area: checkbox;
  align-self: flex-start;
`;

const StyledCheckIcon = styled(Icon)`
  align-self: flex-start;
`;

const StyledHeaderContainerDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
`;

const StyledLoader = styled(Loader)`
  grid-area: chevron;
  margin: 5px;
`;

const StyledChevronIcon = styled(Icon)`
  grid-area: chevron;
  height: 20px;
  width: 20px;
  margin: 5px;
`;

const StyledDetailsTooltipCoordinator = styled(TooltipCoordinator)`
  margin: 0 4px;
`;

export const OnboardingGuideTask: FC<OnboardingGuideTaskProps> = (props) => {
  const {isComplete, renderDetails} = props;
  const theme = useUserTheme();

  const [isLoading, setIsLoading] = useState(false);

  const onClick = async () => {
    if (isLoading) {
      return;
    }

    const maybePromise = props.onClick();
    if (!maybePromise) {
      return;
    }

    setIsLoading(true);
    try {
      await maybePromise;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <StyledChromelessButton onClick={onClick} $isComplete={isComplete} $isCompact={props.isCompact}>
      <TaskCircle isComplete={isComplete} theme={theme} stepNum={props.stepNum} />

      <StyledContentWrapper>
        <StyledTextWrapper>
          <StyledHeaderContainerDiv>
            <StyledTaskTitle>{props.title}</StyledTaskTitle>
            {renderDetails && (
              <StyledDetailsTooltipCoordinator render={renderDetails} maxWidth={299} placement="top" />
            )}
          </StyledHeaderContainerDiv>
          <StyledTaskDescription>{props.description}</StyledTaskDescription>
        </StyledTextWrapper>
      </StyledContentWrapper>
      {isLoading ? (
        <StyledLoader variant={LoaderVariantsEnum.GREY} size={VisualSizesEnum.SMALL} />
      ) : (
        <StyledChevronIcon name="chevronRightTiny" color={theme.palette.grey.shade40} />
      )}
    </StyledChromelessButton>
  );
};

/*
 * Helpers.
 */

interface TaskCircleProps {
  isComplete: boolean;
  theme: UserTheme;
  stepNum?: number;
}

const TaskCircle: FC<TaskCircleProps> = ({isComplete, theme, stepNum}) => {
  const transitions = useTransition(isComplete, {
    // https://github.com/pmndrs/react-spring/issues/2007#issuecomment-1366405164 bug with position in library
    initial: {position: 'absolute' as const, opacity: 1},
    from: {position: 'absolute' as const, opacity: 0},
    enter: {opacity: 1},
    leave: {opacity: 0},
    delay: 500,
    config: {
      duration: 1000,
    },
  });

  return (
    <StyledTaskCircleContainerDiv>
      {transitions((styles, completed) => (
        <animated.div style={styles}>
          {completed ? (
            <StyledCheckIcon name="checkmarkCircleSmall" color={theme.palette.green.shade40} />
          ) : (
            <StyledTaskCircleDiv>{stepNum}</StyledTaskCircleDiv>
          )}
        </animated.div>
      ))}
    </StyledTaskCircleContainerDiv>
  );
};
